import React from "react";
import {
  Chip,
  LiteratureReviewIcon,
  MedicalIcon,
  MetaAnalysisIcon,
  RigorousJournalIcon,
  TextInput,
  VeryRigorousJournalIcon,
} from "design_system";
import { LucideProps } from "lucide-react";
import useLabels from "hooks/useLabels";
import { Role } from "../../types/onboarding";

interface RoleOption {
  value: NonNullable<Role>;
  icon?: React.ReactElement<LucideProps>;
}

const roleOptions: RoleOption[] = [
  {
    value: "undergrad",
    icon: <RigorousJournalIcon />,
  },
  {
    value: "graduate-student",
    icon: <VeryRigorousJournalIcon />,
  },
  {
    value: "faculty",
    icon: <LiteratureReviewIcon />,
  },
  {
    value: "clinician",
    icon: <MedicalIcon />,
  },
  {
    value: "professional",
    icon: <MetaAnalysisIcon />,
  },
  {
    value: "other",
  },
];

type RoleSelectorBaseProps = {
  selectedRole: Role | null;
  onRoleSelect: (role: Role) => void;
  isDisabled?: boolean;
  centerAlign?: boolean;
};

type RoleSelectorWithOtherInput = RoleSelectorBaseProps & {
  hideOtherRoleInput?: false;
  otherRoleText: string;
  onOtherRoleTextChange: (text: string) => void;
};

type RoleSelectorWithoutOtherInput = RoleSelectorBaseProps & {
  hideOtherRoleInput: true;
  otherRoleText?: never;
  onOtherRoleTextChange?: never;
};

type RoleSelectorProps =
  | RoleSelectorWithOtherInput
  | RoleSelectorWithoutOtherInput;

export function RoleSelector(props: RoleSelectorProps) {
  const {
    selectedRole,
    onRoleSelect,
    isDisabled = false,
    centerAlign = true,
  } = props;

  const [authLabels] = useLabels("auth");

  const hideOtherRoleInput = props.hideOtherRoleInput ?? false;
  const otherRoleText = !hideOtherRoleInput ? props.otherRoleText : "";
  const onOtherRoleTextChange = !hideOtherRoleInput
    ? props.onOtherRoleTextChange
    : undefined;
  return (
    <div className="flex flex-col gap-4">
      {/* Role Options */}
      <div
        className={`flex flex-wrap gap-3 ${centerAlign ? "justify-center" : "justify-start"} w-full`}
      >
        {roleOptions.map((option) => (
          <Chip
            key={option.value}
            onClick={() => onRoleSelect(option.value)}
            isDisabled={isDisabled}
            isSelected={selectedRole === option.value}
            iconLeft={option.icon}
            size="base"
          >
            {authLabels.onboarding.roles[option.value]}
          </Chip>
        ))}
      </div>

      {/* Other Role Text Input */}
      {selectedRole === "other" && !hideOtherRoleInput && (
        <div className="w-full">
          <TextInput
            id="other-role-input"
            value={otherRoleText ?? ""}
            onChange={(e) => onOtherRoleTextChange?.(e.target.value)}
            placeholder={authLabels.onboarding.placeholders["other-role"]}
            isDisabled={isDisabled}
          />
        </div>
      )}
    </div>
  );
}
